import React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEstadoDasFontes } from "@/hooks/useEstadoDasFontes";
import {
  motivoDoIndisponivel,
  motivoPedeAtencao,
  requisitosFaltando,
  textoDoMotivo,
  type RequisitoDeFonte,
} from "@/lib/motivo-do-indisponivel";
import { cn } from "@/lib/utils";

/**
 * Os três estados de um número de card: carregando, indisponível e disponível.
 *
 * Antes daqui, cada tela desenhava o seu N/D (nove jeitos diferentes, com quatro textos de
 * tooltip) e várias mostravam N/D **enquanto a requisição ainda estava em voo** — o usuário
 * não tinha como saber se o número vinha ou não. Agora o carregamento tem cara própria, e o
 * N/D diz o motivo.
 */

type CarregandoValorProps = {
  className?: string;
  /** Texto lido pelo leitor de tela. */
  rotulo?: string;
};

/** Indicador circular no lugar do número, do tamanho do texto em volta. */
export function CarregandoValor({
  className,
  rotulo = "Carregando dados",
}: CarregandoValorProps) {
  return (
    <span
      role="status"
      className={cn(
        "inline-flex items-center text-muted-foreground",
        className,
      )}
    >
      <Loader2
        aria-hidden="true"
        className="h-[1em] min-h-4 w-[1em] min-w-4 animate-spin"
      />
      <span className="sr-only">{rotulo}</span>
    </span>
  );
}

/**
 * Véu com indicador sobre um card que **já tem** números e está buscando outros (troca de
 * filtro). Os números antigos continuam visíveis por baixo, mas deixa de parecer que são os
 * do filtro novo. O pai precisa ser `relative`.
 */
export function CarregandoSobreposto({ visivel }: { visivel: boolean }) {
  if (!visivel) return null;
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-background/50 backdrop-blur-[1px]">
      <CarregandoValor
        className="text-2xl text-primary"
        rotulo="Atualizando dados"
      />
    </div>
  );
}

type ValorIndisponivelProps = {
  /** O que o número precisa. Ver `RequisitoDeFonte`. Vazio = não depende de integração. */
  fontes?: readonly RequisitoDeFonte[];
  /** A requisição que traria o número falhou. */
  erro?: boolean;
  className?: string;
  /** Classe do ícone "!" — para fundos escuros, como a imagem do funil. */
  iconeClassName?: string;
  side?: "top" | "right" | "bottom" | "left";
};

/** "N/D" com um "!" cujo tooltip explica por que o número não veio. */
export function ValorIndisponivel({
  fontes = [],
  erro = false,
  className,
  iconeClassName,
  side = "top",
}: ValorIndisponivelProps) {
  const estado = useEstadoDasFontes();
  const motivo = motivoDoIndisponivel({ requisitos: fontes, estado, erro });
  const texto = textoDoMotivo(motivo);
  const atencao = motivoPedeAtencao(motivo);

  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      N/D
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`Por que este número está indisponível: ${texto}`}
            className={cn(
              "inline-flex shrink-0 items-center justify-center rounded-full transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              atencao
                ? "text-amber-500 hover:text-amber-600"
                : "text-muted-foreground hover:text-foreground",
              iconeClassName,
            )}
          >
            <AlertCircle aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className="max-w-[320px] text-xs font-normal leading-snug"
        >
          <p>{texto}</p>
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

type EstadoDoValorProps = Omit<ValorIndisponivelProps, "className"> & {
  carregando: boolean;
  /** `false` desenha o N/D. */
  disponivel: boolean;
  children: React.ReactNode;
  /** Classe aplicada ao indicador e ao N/D (tamanho/cor do texto do card). */
  className?: string;
};

/**
 * Carregando → indicador; indisponível → N/D com motivo; senão, o número.
 *
 * Um número cuja fonte a loja **não integrou** também sai como N/D, mesmo que a rota tenha
 * respondido: ela devolve zero nesse caso, e o zero não foi medido. Enquanto a lista de
 * contas da loja não voltou, o número é mostrado como veio.
 */
export function EstadoDoValor({
  carregando,
  disponivel,
  children,
  className,
  ...indisponivel
}: EstadoDoValorProps) {
  const estado = useEstadoDasFontes();
  const faltaIntegrar =
    estado !== null &&
    requisitosFaltando(indisponivel.fontes ?? [], estado).length > 0;

  if (carregando) return <CarregandoValor className={className} />;
  if (!disponivel || faltaIntegrar) {
    return <ValorIndisponivel className={className} {...indisponivel} />;
  }
  return <>{children}</>;
}
